import axios from 'axios';
import { toast } from 'sonner';
import { getPortalToken, clearPortalSession, isPortalPath } from '../pages/Portal/portalStorage';
import { clearLegacyAuthStorage } from './clearLegacyAuthStorage';

// Empty REACT_APP_BACKEND_URL → same-origin /api/v1 (nginx or CRA proxy).
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const api = axios.create({
  baseURL: `${BACKEND_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // HttpOnly session cookies (admin + /m)
});

/**
 * Uploads: den Standard-Header „application/json“ wieder abnehmen.
 *
 * Axios prüft in `transformRequest` den Content-Type, *bevor* es die Nutzlast
 * anfasst. Steht dort „application/json“ und die Nutzlast ist FormData, macht
 * es daraus JSON:
 *
 *     if (isFormData) return hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data;
 *
 * Die Datei ist damit weg, beim Server landet `{"file":{}}`, und FastAPI
 * antwortet mit 422. Der Rettungsanker weiter unten in `resolveConfig` (dort
 * nimmt Axios den Content-Type für FormData ab, damit der Browser ihn samt
 * Boundary setzt) greift nicht mehr — die Nutzlast ist zu dem Zeitpunkt längst
 * eine Zeichenkette.
 *
 * Deshalb hier, vor `transformRequest`. Aufrufer müssen den Header nicht mehr
 * selbst überschreiben; der Browser setzt ihn mit Boundary.
 */
function contentTypeFuerUploadsEntfernen(config) {
  if (typeof FormData === 'undefined' || !(config.data instanceof FormData)) return;
  if (typeof config.headers?.delete === 'function') config.headers.delete('Content-Type');
  else if (config.headers) delete config.headers['Content-Type'];
}

api.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    contentTypeFuerUploadsEntfernen(config);
    const onPortalPage = isPortalPath();
    const isPortalApi = url.includes('/portal/') || url.startsWith('portal/');

    // Eltern-Portal: still Bearer (portal_token) — separate auth surface
    if (onPortalPage || isPortalApi) {
      const portalToken = getPortalToken();
      if (portalToken) {
        config.headers.Authorization = `Bearer ${portalToken}`;
      }
    }
    // Admin / mobile web: cookie only — never attach localStorage JWTs

    const tenantId = localStorage.getItem('active_tenant_id');
    if (tenantId && !(onPortalPage || isPortalApi)) {
      config.headers['X-Tenant-ID'] = tenantId;
      const senderConfigId = localStorage.getItem(`email_sender_config_id:${tenantId}`);
      if (senderConfigId) {
        config.headers['X-Email-Config-ID'] = senderConfigId;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/** Pages that must stay usable without a session (401 from /auth/me must not bounce away). */
export function isPublicAuthPath(pathname) {
  if (!pathname) return false;
  if (pathname === '/login' || pathname.startsWith('/m/login')) return true;
  if (pathname === '/passwort-vergessen' || pathname === '/passwort-zuruecksetzen') return true;
  if (pathname.startsWith('/portal/login')) return true;
  if (pathname.startsWith('/anmeldung')) return true;
  if (pathname.startsWith('/konferenz/')) return true;
  if (pathname.startsWith('/teacher-slot/') || pathname.startsWith('/parent-slot/')) return true;
  if (pathname.startsWith('/steuerberater/')) return true;
  return false;
}

let _redirectingDueTo401 = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isLoginCall =
        url.includes('/auth/login') || url.includes('/portal/login') || url.includes('/app/login');
      const onPortal = isPortalPath();
      const path = window.location.pathname;

      if (!isLoginCall && !_redirectingDueTo401 && !isPublicAuthPath(path)) {
        if (onPortal || url.includes('/portal/')) {
          _redirectingDueTo401 = true;
          clearPortalSession();
          toast.error('Sitzung abgelaufen — bitte erneut anmelden.', { duration: 4000 });
          setTimeout(() => { window.location.href = '/portal/login'; }, 600);
        } else if (!path.startsWith('/m/login')) {
          _redirectingDueTo401 = true;
          clearLegacyAuthStorage();
          localStorage.removeItem('user');
          toast.error('Sitzung abgelaufen — bitte erneut anmelden.', { duration: 4000 });
          const here = path + window.location.search;
          const target = here.startsWith('/m')
            ? '/m/login'
            : `/login?redirect=${encodeURIComponent(here)}`;
          setTimeout(() => { window.location.href = target; }, 600);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
